// printAgent.js — ตัวกลางคุยกับ Packhai Print Agent (desktop app) ผ่าน WebSocket
// ใช้สำหรับ silent print AWB ไปยัง thermal printer โดยตรง
//
// พฤติกรรม:
//   - Probe agent ครั้งเดียวตอน module load (non-blocking) -> cache state
//   - print แต่ละครั้งใช้ค่า cache ไม่ delay
//   - ถ้า agent disconnect/error -> mark unavailable อัตโนมัติ -> caller ได้รับ throw -> fallback ได้

const WS_URL = "ws://127.0.0.1:23445";
const PROBE_TIMEOUT_MS = 1000;
const SEND_TIMEOUT_MS = 10000;

// ชื่อ key ที่เก็บ printer ที่ผู้ใช้เลือกไว้ใน localStorage
const STORAGE_KEY_PRINTER = "labelPrinter";
const STORAGE_KEY_LABEL_SIZE = "labelSize";  // "100x100" | "100x150" | "A4" | "A6" | "WxH"
const STORAGE_KEY_DISABLED = "printAgentDisabled"; // "1" = user กดยกเลิกการเชื่อมต่อไว้
const DEFAULT_LABEL_SIZE = "100x150";

// Preset ขนาดกระดาษที่ UI ใช้แสดง — ขยายได้ในอนาคต
export const LABEL_SIZE_PRESETS = [
  { id: "100x100", name: "100 × 100 mm", widthMm: 100, heightMm: 100 },
  { id: "100x150", name: "100 × 150 mm", widthMm: 100, heightMm: 150 },
  { id: "A6",      name: "A6 (105 × 148 mm)", widthMm: 105, heightMm: 148 },
  { id: "A4",      name: "A4 (210 × 297 mm)", widthMm: 210, heightMm: 297 },
];

/**
 * parse label size string -> { widthMm, heightMm, name }
 * รองรับทั้ง preset id ("100x100", "A4") และ custom "WxH"
 */
export function parseLabelSize(sizeStr) {
  if (!sizeStr) return null;
  // หา preset ก่อน
  const preset = LABEL_SIZE_PRESETS.find((p) => p.id === sizeStr);
  if (preset) return { widthMm: preset.widthMm, heightMm: preset.heightMm, name: preset.name };
  // custom format "WxH"
  const match = String(sizeStr).match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i);
  if (match) {
    const w = parseFloat(match[1]);
    const h = parseFloat(match[2]);
    return { widthMm: w, heightMm: h, name: `${w} × ${h} mm` };
  }
  return null;
}

function isManuallyDisabled() {
  try { return localStorage.getItem(STORAGE_KEY_DISABLED) === "1"; }
  catch (err) {
    console.warn("[printAgent] read localStorage disabled flag failed:", err);
    return false;
  }
}

let ws = null;
let state = "unknown"; // "unknown" | "ready" | "unavailable"
const pending = new Map();
const stateListeners = new Set();

function setState(newState) {
  if (state === newState) return;
  state = newState;
  for (const listener of stateListeners) {
    try { listener(state); } catch (err) {
      console.warn("[printAgent] state listener threw:", err);
    }
  }
}

function genId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return String(Date.now()) + "-" + Math.random().toString(36).slice(2);
}

function handleMessage(event) {
  let msg;
  try {
    msg = JSON.parse(event.data);
  } catch (err) {
    console.warn("[printAgent] invalid JSON from agent:", err, event.data);
    return;
  }
  const p = pending.get(msg.id);
  if (!p) return;
  pending.delete(msg.id);
  if (msg.ok) p.resolve(msg.result);
  else p.reject(new Error(msg.error || "agent error"));
}

function _connect(timeoutMs = PROBE_TIMEOUT_MS) {
  // ถ้าเชื่อมอยู่แล้วไม่ต้องทำซ้ำ
  if (ws && ws.readyState === WebSocket.OPEN) return Promise.resolve();

  return new Promise((resolve, reject) => {
    let socket;
    try {
      socket = new WebSocket(WS_URL);
    } catch (e) {
      setState("unavailable");
      return reject(e);
    }
    const timer = setTimeout(() => {
      setState("unavailable");
      try { socket.close(); } catch (err) { console.warn("[printAgent] close on timeout failed:", err); }
      reject(new Error("agent connect timeout"));
    }, timeoutMs);

    socket.onopen = () => {
      clearTimeout(timer);
      ws = socket;
      setState("ready");
      resolve();
    };
    socket.onerror = () => {
      clearTimeout(timer);
      setState("unavailable");
      reject(new Error("agent connect error"));
    };
    socket.onclose = () => {
      setState("unavailable");
      ws = null;
      // reject ทุก request ที่ค้างอยู่
      for (const [, p] of pending) p.reject(new Error("agent disconnected"));
      pending.clear();
    };
    socket.onmessage = handleMessage;
  });
}

function send(action, data) {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return reject(new Error("agent not connected"));
    }
    const id = genId();
    pending.set(id, { resolve, reject });
    const timer = setTimeout(() => {
      if (pending.has(id)) {
        pending.delete(id);
        reject(new Error("agent send timeout"));
      }
    }, SEND_TIMEOUT_MS);
    // wrap resolve/reject เพื่อ clear timer
    const orig = pending.get(id);
    pending.set(id, {
      resolve: (v) => { clearTimeout(timer); orig.resolve(v); },
      reject: (e) => { clearTimeout(timer); orig.reject(e); },
    });

    try {
      ws.send(JSON.stringify({ id, action, data }));
    } catch (e) {
      clearTimeout(timer);
      pending.delete(id);
      reject(e);
    }
  });
}

function uint8ToBase64(uint8) {
  let binary = "";
  const len = uint8.length;
  const chunk = 0x8000;
  for (let i = 0; i < len; i += chunk) {
    binary += String.fromCharCode.apply(null, uint8.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Probe ครั้งเดียวตอน module load — non-blocking (ไม่ throw)
// ถ้า user กดยกเลิกการเชื่อมต่อไว้ใน window อื่น (localStorage flag) ข้ามไป
if (!isManuallyDisabled()) {
  _connect().catch(() => {});
} else {
  state = "unavailable";
}

export const printAgent = {
  /** เครื่องนี้พร้อมใช้ silent print ไหม? (sync, อ่านจาก cache) */
  isReady() {
    return state === "ready";
  },

  /** state ปัจจุบัน: "unknown" | "ready" | "unavailable" */
  getState() {
    return state;
  },

  /**
   * เชื่อมต่อ agent ใหม่ (ใช้ตอนกดปุ่ม "เชื่อมต่อ")
   * @returns {Promise<boolean>} true = สำเร็จ, false = ไม่สำเร็จ
   */
  async connect(timeoutMs = 3000) {
    // user opt-in กลับ -> เคลียร์ flag เพื่อให้ window อื่น probe ได้ปกติ
    try {
      localStorage.removeItem(STORAGE_KEY_DISABLED);
    } catch (err) {
      console.warn("[printAgent] clear disabled flag failed:", err);
    }
    try {
      await _connect(timeoutMs);
      return true;
    } catch (err) {
      console.warn("[printAgent] connect failed:", err.message || err);
      return false;
    }
  },

  /** ตัดการเชื่อมต่อ agent (ใช้ตอนกดปุ่ม "ตัดการเชื่อมต่อ") */
  disconnect() {
    // ตั้ง flag ใน localStorage ให้ window อื่น (เช่น /print-awb) เห็นด้วย
    // ไม่งั้นจะ probe ใหม่ทุกครั้งที่เปิด window -> ยังคุย agent อยู่
    try {
      localStorage.setItem(STORAGE_KEY_DISABLED, "1");
    } catch (err) {
      console.warn("[printAgent] set disabled flag failed:", err);
    }

    // sync update state + clear ws ก่อน — ป้องกัน race ตอน ws.close() ยัง async อยู่
    const oldWs = ws;
    ws = null;
    setState("unavailable");
    // reject pending request ที่ค้างอยู่ทันที
    for (const [, p] of pending) p.reject(new Error("agent disconnected"));
    pending.clear();
    if (oldWs) {
      try {
        oldWs.close();
      } catch (err) {
        console.warn("[printAgent] close socket on disconnect failed:", err);
      }
    }
  },

  /**
   * สมัครฟัง state change — ใช้ตอน Vue component อยากรู้ตอน agent connect/disconnect
   * @param {(state: string) => void} listener
   * @returns {() => void} เรียกเพื่อยกเลิกการ subscribe
   */
  onStateChange(listener) {
    stateListeners.add(listener);
    return () => stateListeners.delete(listener);
  },

  /** ชื่อ printer ที่ผู้ใช้ตั้งไว้ใน localStorage */
  getConfiguredPrinter() {
    try {
      return localStorage.getItem(STORAGE_KEY_PRINTER) || null;
    } catch (err) {
      console.warn("[printAgent] read configured printer failed:", err);
      return null;
    }
  },

  /** บันทึก printer ที่ user เลือก */
  setConfiguredPrinter(name) {
    try {
      localStorage.setItem(STORAGE_KEY_PRINTER, name);
    } catch (err) {
      console.warn("[printAgent] save configured printer failed:", err);
    }
  },

  /** ขนาดกระดาษที่ user เลือก: preset id หรือ "WxH" custom (default "100x150" ถ้ายังไม่ได้ตั้ง) */
  getLabelSize() {
    try {
      return localStorage.getItem(STORAGE_KEY_LABEL_SIZE) || DEFAULT_LABEL_SIZE;
    } catch (err) {
      console.warn("[printAgent] read label size failed:", err);
      return DEFAULT_LABEL_SIZE;
    }
  },

  /** ผู้ใช้เคยตั้งค่า label size แล้วหรือยัง (แยกจาก default) */
  hasLabelSizeConfigured() {
    try {
      return localStorage.getItem(STORAGE_KEY_LABEL_SIZE) !== null;
    } catch (err) {
      console.warn("[printAgent] read label size flag failed:", err);
      return false;
    }
  },

  /** บันทึกขนาดกระดาษ */
  setLabelSize(size) {
    try {
      localStorage.setItem(STORAGE_KEY_LABEL_SIZE, size);
    } catch (err) {
      console.warn("[printAgent] save label size failed:", err);
    }
  },

  /** ดึงรายชื่อ printer (throw ถ้า agent ไม่พร้อม) */
  async listPrinters() {
    if (state !== "ready") throw new Error("agent unavailable");
    const res = await send("listPrinters");
    return res.printers || [];
  },

  /**
   * เช็คสถานะ printer ตัวที่ระบุ — ใช้ตรวจว่ายังเสียบ USB / online อยู่ไหม
   * @returns {Promise<"ready"|"offline"|"notfound">}
   */
  async checkPrinter(printerName) {
    if (state !== "ready") throw new Error("agent unavailable");
    const res = await send("checkPrinter", { printerName });
    return res.status || "notfound";
  },

  /**
   * Proxy fetch URL ผ่าน agent — ใช้ดึงรูปที่ติด CORS
   * agent ไม่อยู่ใน browser sandbox จึง fetch ได้ทุก URL
   * @param {string} url
   * @returns {Promise<{ contentType: string, base64: string, size: number }>}
   */
  async fetchUrl(url) {
    if (state !== "ready") throw new Error("agent unavailable");
    return send("fetchUrl", { url });
  },

  /**
   * ปริ้น PDF แบบ silent ผ่าน agent
   * @param {Uint8Array|ArrayBuffer} pdfBytes
   * @throws ถ้า agent ไม่พร้อม/ไม่มี printer/ส่งล้มเหลว -> caller ต้อง fallback เอง
   */
  async printPDF(pdfBytes, options = {}) {
    if (state !== "ready") throw new Error("agent unavailable");
    const printerName = this.getConfiguredPrinter();
    if (!printerName) throw new Error("no printer configured");

    const uint8 = pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array(pdfBytes);
    const pdfBase64 = uint8ToBase64(uint8);

    // ถ้าผู้ใช้ตั้ง paper size ไว้ -> ส่งไป agent ด้วย -> agent จะใช้ bitmap path
    // (PDFtoImage render PDF -> image -> PrintDocument กับ paper size เป๊ะ)
    // ถ้าไม่มี -> agent จะใช้ shell PrintTo verb (backward compatible)
    const sizeStr = options.labelSize || this.getLabelSize();
    const parsed = parseLabelSize(sizeStr);
    const payload = { printerName, pdfBase64, options };
    if (parsed) {
      payload.paperWidthMm = parsed.widthMm;
      payload.paperHeightMm = parsed.heightMm;
    }
    return send("printPDF", payload);
  },

  /**
   * ปริ้นรูปภาพ (PNG/JPG) แบบ silent ผ่าน agent
   * C# agent จะใช้ System.Drawing.Printing.PrintDocument ส่งตรงไปที่ printer driver
   * ไม่พึ่ง PDF handler (Adobe/Edge) — เหมาะกับ thermal printer
   * @param {string} imageBase64  base64 ของไฟล์รูป (ไม่มี data:... prefix)
   * @param {object} [options]
   * @param {number} [options.paperWidth]   ความกว้างกระดาษ (หน่วย 1/100 inch — 400 = 4 นิ้ว ≈ 100mm)
   * @param {number} [options.paperHeight]  ความสูงกระดาษ (หน่วย 1/100 inch)
   */
  async printImage(imageBase64, options = {}) {
    if (state !== "ready") throw new Error("agent unavailable");
    const printerName = this.getConfiguredPrinter();
    if (!printerName) throw new Error("no printer configured");
    return send("printImage", {
      printerName,
      imageBase64,
      paperWidth: options.paperWidth,
      paperHeight: options.paperHeight,
    });
  },

  /** ปริ้น raw ZPL command */
  async printZPL(zpl) {
    if (state !== "ready") throw new Error("agent unavailable");
    const printerName = this.getConfiguredPrinter();
    if (!printerName) throw new Error("no printer configured");
    return send("printZPL", { printerName, zpl });
  },
};

export default printAgent;
